import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import setCookieForHeader from "@/hooks/api/cookie/get-cookie";
import { RootState } from "@/store/root-reducer";
import { GetQuoteFilter } from "@/store/slices/quote/API/getQuote";
import { getQuoteData } from "@/store/slices/quote/quote.action";
import { AppDispatch } from "@/store/store";
import { storageAvailable } from "@/utils/common.utils";

const withQuestionData = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  function WithSessionData(props: Readonly<P>) {
    const dispatch = useDispatch<AppDispatch>();
    const router = useRouter();
    const searchParams = useSearchParams();
    const quoteResponse = useSelector((state: RootState) => state.quote);
    const stringWithoutSlashes =
      typeof window !== "undefined"
        ? window.location.pathname.replace(/\//g, "")
        : "";

    const quoteRef = searchParams.get("quoteId");
    const affiliate = searchParams.get("affiliate");
    const pathname = usePathname();

    useEffect(() => {
      if (quoteResponse) {
        const { referenceId } = quoteResponse;

        const handleBeforeUnload = (event: { preventDefault: () => void }) => {
          if (storageAvailable()) {
            sessionStorage.setItem("pageReload", "true");
          }

          if (referenceId && storageAvailable()) {
            event.preventDefault();
            sessionStorage.removeItem("organiserTravellerId");
          }
        };

        const isOnIOS =
          navigator.userAgent.match(/iPad/i) ||
          navigator.userAgent.match(/Macintosh/i) ||
          navigator.userAgent.match(/iPhone/i);
        const eventName = isOnIOS ? "pagehide" : "beforeunload";

        window.addEventListener(eventName, handleBeforeUnload);
        return () => {
          return window.removeEventListener(eventName, handleBeforeUnload);
        };
      }

      return undefined;
    }, [stringWithoutSlashes, quoteResponse]);

    function quoteCallCheck() {
      if (
        (storageAvailable() && sessionStorage.getItem("pageReload")) ||
        !sessionStorage.getItem("initialLoad")
      ) {
        return true;
      }

      return false;
    }

    const fetchQuoteDataComponent = async () => {
      let getQuoteFilter: GetQuoteFilter = {
        affiliate: null,
        quoteId: null,
      };

      if (pathname === "/") {
        await setCookieForHeader();
      }

      if (!affiliate && !quoteRef) {
        getQuoteFilter.affiliate = "quoteInit";
      } else {
        getQuoteFilter = {
          affiliate,
          quoteId: quoteRef,
        };
      }

      if (quoteCallCheck() && !quoteResponse.referenceId) {
        if (stringWithoutSlashes) {
          sessionStorage.setItem("initialLoad", "true");
          sessionStorage.removeItem("pageReload");
          await dispatch(getQuoteData({ getQuoteFilter, router, dispatch }));
        }
      }
      return null; // Ensure the function returns null if no action is needed
    };

    useEffect(() => {
      fetchQuoteDataComponent();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...props} />;
  }

  return WithSessionData;
};

export default withQuestionData;
